import { getPianoEnv } from "@sciam/piano";
import styles from "./paywallModal.module.css";

const offerIds = {
  // $30 for 6 months Unlimited
  sixMonths30: {
    sandbox: "OF9ESBJPRSI7",
    prod: "OFG9AV8FZIN7",
  },
  // 90 day for $1
  ninetyDay: {
    sandbox: "OFJ3XP10V2EP",
    prod: "OF4VU8L2TA66",
  },
  // 60 day for $1
  sixtyDay: {
    sandbox: "OFIBEPVKC8G3",
    prod: "OFL7Z5LL7EPF",
  },
  // 30 day trial
  thirtyDay: {
    sandbox: "OFICBPF0RDIL",
    prod: "OFLS89VO4IQR",
  },
  // Regular digital purchase
  current: {
    sandbox: "OF4WYQSD3HZN",
    preprod: "OF85KSMYIFWO",
    prod: "OFT7IPXNYFO0",
  },
};

const termIds = {
  // $30 for 6 months Unlimited
  sixMonths30: {
    sandbox: "TMZPBID6HIFQ",
    prod: "TMBZ9YM09TE1",
  },
  // 90 day for $1
  ninetyDay: {
    sandbox: "TMAVS5UY3N7H",
    prod: "TMNJQCECGZBL",
  },
  // 60 day for $1
  sixtyDay: {
    sandbox: "TMVEMVWDNKDI",
    prod: "TMB5Y33ULRRF",
  },
  // 30 day trial
  thirtyDay: {
    sandbox: "TMP4C89FDJQ8",
    prod: "TM6XHODW4W7C",
  },
  // Regular digital purchase
  current: {
    sandbox: "TMGUKMK9011K",
    preprod: "TM1L5ZHXONWR",
    prod: "TMEXQMQN5ELD",
  },
};

/**
 * @typedef {Object} PaywallScreenConfig - copy and config required to render paywall
 *
 * Copy
 * ====
 *
 * @property {string} mainText - The primary headline text for the subscription offer
 * @property {string} subText - The secondary text describing the subscription deal
 * @property {string[]} benefits - Array of subscription benefits/features
 * @property {string} buttonCTA - Call-to-action text for the subscription button
 * @property {string} finePrint - Legal text containing subscription renewal terms and cancellation policy
 *
 * Data
 * ====
 * @property {Object} datalayer - Analytics data configuration
 * @property {string} datalayer.modalName - Name identifier for the subscription modal
 *
 * Chargebee
 * =========
 * @property {string} cbPlanId - ChargeBee's item price ID, e.g., DIGITAL_90DAY_P
 *
 * Piano
 * =====
 * @property {string} offerId - Unique identifier for the subscription offer from Piano
 * @property {string} termId - Unique identifier for the subscription term from Piano
 * @property {string} [promoCode] - Optional promotional code for the offer
 */

/**
 * Default, Digital discounted
 * @param {string} envPiano
 * @returns {PaywallScreenConfig}
 */
const DEFAULT_PAYWALL_LANGUAGE = (envPiano) => {
  return {
    mainText: "Save 30% on a Digital Subscription",
    subText:
      "You must be a subscriber to continue reading.<br/>Subscribe today for just <s>$39.00</s> $27 for the first year.",
    benefits: [
      "Digital access to all articles and monthly issues",
      "Digital archive access from 1845 to today with more than 7,000 issues!",
      "Scientific American iOS and Android app",
    ],
    buttonCTA: "Subscribe",
    finePrint: (
      <p>
        Your subscription will automatically begin at a rate of $27 for your first year, plus sales
        tax where applicable. After your initial term, your subscription will automatically renew at
        a rate of $39 per year, plus sales tax where applicable until you cancel. We will email you
        a reminder prior to renewal with the renewal rate and date of expected charge. If you do
        nothing, we will charge the card entered here and your subscription will be renewed. You can
        cancel your subscription at any time in your{" "}
        <a href="https://www.scientificamerican.com/account/" className={styles.trialHelpEmailLnk}>
          subscription management page
        </a>
        . Cancellations will take place at the end of your current subscription term. If you have
        any questions, email us at{" "}
        <a href="mailto:help@sciam.com" className={styles.trialHelpEmailLnk}>
          help@sciam.com
        </a>
        .
      </p>
    ),
    offerId: offerIds["current"][envPiano],
    termId: termIds["current"][envPiano],
    promoCode: "digital2023",
    datalayer: {
      modalName: "currentoffer",
    },
    cbPlanId: "DIGITAL_1YEARDISCOUNT_P",
  };
};

/**
 * 30 Day Free Trial
 * @param {string} envPiano
 * @returns {PaywallScreenConfig}
 */
const THIRTY_DAY_TRIAL_LANGUAGE = (envPiano) => {
  return {
    mainText: "A Subscription Is Required to Continue Reading",
    subText: "How does a free month on us sound?",
    benefits: [
      "Digital access to all articles and monthly issues",
      "Digital archive access from 1845 to today with more than 7,000 issues!",
      "Cancel anytime",
    ],
    buttonCTA: "Start Free 30-Day Trial",
    finePrint: (
      <p>
        At the end of your free trial, your subscription will automatically begin at a rate of
        $39.00 a year, plus sales tax where applicable. Your subscription will automatically renew
        at a rate of $39 per year, plus sales tax where applicable until you cancel. We will email
        you a reminder prior to renewal with the renewal rate and date of expected charge. If you do
        nothing, we will charge the card entered here and your subscription will be renewed. You can
        cancel your subscription at any time in your{" "}
        <a href="https://www.scientificamerican.com/account/" className={styles.trialHelpEmailLnk}>
          subscription management page
        </a>
        . Cancellations will take place at the end of your current subscription term. If you have
        any questions, email us at{" "}
        <a href="mailto:help@sciam.com" className={styles.trialHelpEmailLnk}>
          help@sciam.com
        </a>
        .
      </p>
    ),
    offerId: offerIds["thirtyDay"][envPiano],
    termId: termIds["thirtyDay"][envPiano],
    promoCode: "",
    datalayer: {
      modalName: "thirtyDay",
    },
    cbPlanId: "DIGITAL_30DAY_P",
  };
};

/**
 * 60-day for $1
 * @param {string} envPiano
 * @returns {PaywallScreenConfig}
 */
const SIXTY_DAY_ONE_DOLLAR_LANGUAGE = (envPiano) => {
  return {
    mainText: "A Subscription Is Required to Continue Reading",
    subText: "How does 60 days for $1 sound?",
    benefits: [
      "Digital access to all articles and monthly issues",
      "Digital archive access from 1845 to today with more than 7,000 issues!",
      "Cancel anytime",
    ],
    buttonCTA: "Start for $1",
    finePrint: (
      <p>
        At the end of your 60 days of digital access, your subscription will automatically renew at
        a rate of $39 per year, plus sales tax where applicable until you cancel. We will email you
        a reminder prior to renewal with the renewal rate and date of expected charge. If you do
        nothing, we will charge the card entered here and your subscription will be renewed. You can
        cancel your subscription at any time in your{" "}
        <a href="https://www.scientificamerican.com/account/" className={styles.trialHelpEmailLnk}>
          subscription management page
        </a>
        . Cancellations will take place at the end of your current subscription term. If you have
        any questions, email us at{" "}
        <a href="mailto:help@sciam.com" className={styles.trialHelpEmailLnk}>
          help@sciam.com
        </a>
        .
      </p>
    ),
    offerId: offerIds["sixtyDay"][envPiano],
    termId: termIds["sixtyDay"][envPiano],
    promoCode: "",
    datalayer: {
      modalName: "sixtyDay",
    },
    cbPlanId: "DIGITAL_60DAY_P",
  };
};

/**
 * 90-day for $1
 * @param {string} envPiano
 * @returns {PaywallScreenConfig}
 */
const NINETY_DAY_ONE_DOLLAR_LANGUAGE = (envPiano) => {
  return {
    mainText: "A Subscription Is Required to Continue Reading",
    subText: "How does 90 days for $1 sound?",
    benefits: [
      "Digital access to all articles and monthly issues",
      "Digital archive access from 1845 to today with more than 7,000 issues!",
      "Cancel anytime",
    ],
    buttonCTA: "Start for $1",
    finePrint: (
      <p>
        At the end of your 90 days of digital access, your subscription will automatically renew at
        a rate of $39 per year, plus sales tax where applicable until you cancel. We will email you
        a reminder prior to renewal with the renewal rate and date of expected charge. If you do
        nothing, we will charge the card entered here and your subscription will be renewed. You can
        cancel your subscription at any time in your{" "}
        <a href="https://www.scientificamerican.com/account/" className={styles.trialHelpEmailLnk}>
          subscription management page
        </a>
        . Cancellations will take place at the end of your current subscription term. If you have
        any questions, email us at{" "}
        <a href="mailto:help@sciam.com" className={styles.trialHelpEmailLnk}>
          help@sciam.com
        </a>
        .
      </p>
    ),
    offerId: offerIds["ninetyDay"][envPiano],
    termId: termIds["ninetyDay"][envPiano],
    promoCode: "",
    datalayer: {
      modalName: "ninetyDay",
    },
    cbPlanId: "DIGITAL_90DAY_P",
  };
};

// 6 months for $30
const SIX_MONTHS_THIRTY_DOLLAR_LANGUAGE = (envPiano) => {
  return {
    mainText: "A Subscription Is Required to Continue Reading",
    subText: "How does 6 months of Unlimited for $30 sound? That's 70% off!",
    benefits: [
      "Monthly print issues",
      "Print and online special editions",
      "Ad-free digital access to all stories",
      "Digital access to gift ($39 value)",
      "SciAm tote bag",
    ],
    buttonCTA: "Subscribe",
    finePrint: (
      <p>
        At the end of your 6 months of digital access, your subscription will automatically renew at
        a rate of $199 per year, plus sales tax where applicable until you cancel. We will email you
        a reminder prior to renewal with the renewal rate and date of expected charge. If you do
        nothing, we will charge the card entered here and your subscription will be renewed. You can
        cancel your subscription at any time in your{" "}
        <a href="https://www.scientificamerican.com/account/" className={styles.trialHelpEmailLnk}>
          subscription management page
        </a>
        . Cancellations will take place at the end of your current subscription term. If you have
        any questions, email us at{" "}
        <a href="mailto:help@sciam.com" className={styles.trialHelpEmailLnk}>
          help@sciam.com
        </a>
        .
      </p>
    ),
    offerId: offerIds["sixMonths30"][envPiano],
    termId: termIds["sixMonths30"][envPiano],
    promoCode: "",
    datalayer: {
      modalName: "sixMonths30",
    },
  };
};

/* Name of the cookie that the email API sets in the browser */
export const emailCollectorCookie = "_emailCollectorRegistered";

/**
 * Get data to display text and pass promo codes for paywall.
 * @param envPiano {string}
 * @param version {("default"|"thirtyDayTrial"|"ninetyDayOneDollar"|"cyberMonday")}
 * @returns {PaywallScreenConfig}
 */
export const getPaywallConfig = (version = "default") => {
  const currentPianoEnv = getPianoEnv().slug;

  if (version === "thirtyDayTrial") {
    return THIRTY_DAY_TRIAL_LANGUAGE(currentPianoEnv);
  }

  if (version === "sixtyDayOneDollar") {
    return SIXTY_DAY_ONE_DOLLAR_LANGUAGE(currentPianoEnv);
  }

  if (version === "ninetyDayOneDollar") {
    return NINETY_DAY_ONE_DOLLAR_LANGUAGE(currentPianoEnv);
  }

  if (version === "sixmonths30") {
    return SIX_MONTHS_THIRTY_DOLLAR_LANGUAGE(currentPianoEnv);
  }

  return DEFAULT_PAYWALL_LANGUAGE(currentPianoEnv);
};
